import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Label, Row } from 'reactstrap';
import api from "../../../../services/api";
import _ from 'lodash';
import { useMsal } from '@azure/msal-react';
import Loader from '../../../loaders';
import { Redirect, useHistory } from "react-router-dom";
import TerminationForm from './TerminationForm';
import TerminationApprovalPanel from './TerminationApprovalPanel';
import { checkApprover, composeDataToSend, returnToList, validate } from './TerminationUtils';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
import ConfirmGeneralAlert from '../../../alerts/ConfirmGeneralAlert';
import { roles } from '../../../../auth/roles';

const apiUrl = "termination";

export default function TerminationInitialForm(props) {
    const {id} = useParams();

    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [loadingCheckApprover, setLoadingCheckApprover] = useState(false);
    const [itemInEdit, setItemInEdit] = useState({});
    const [shouldCheckApprover, setShouldCheckApprover] = useState(false);
    const [initialApprover, setInitialApprover] = useState(null);
    const [isApproverExists, setIsApproverExists] = useState(false);
    const [validationObj, setValidationObj] = useState([]);
    const [showValidation, setShowValidation] = useState(false);
    const [popupMessageObj, setPopupMessageObj] = useState(null);
    const [canSave, setCanSave] = useState(false);
    const { instance } = useMsal();
    const user = instance.getActiveAccount();
    const history = useHistory();

    const hasFullAccess = useMemo(() => {
        return _.intersection([roles.HR, roles.Admin], user.idTokenClaims.roles).length > 0;
    }, [roles, user]);

    const hasAccess = useMemo(() => {
        return _.intersection([roles.HR, roles.Manager, roles.Admin], user.idTokenClaims.roles).length > 0;
    }, [roles, user]);

    useEffect(() => {
        if (!itemInEdit?.lastDayEmploymentDate)
            return;

        if (itemInEdit?.lastDayWorkDate)
            return;

        setItemInEdit({...itemInEdit, lastDayWorkDate: itemInEdit.lastDayEmploymentDate})
    }, [itemInEdit?.lastDayEmploymentDate]);

    useEffect(() => {
        if (!shouldCheckApprover)
            return;

        setShouldCheckApprover(false);
        setLoadingCheckApprover(true);
        checkApprover(itemInEdit, setItemInEdit, setInitialApprover, setIsApproverExists, validationObj, setValidationObj, user, api, apiUrl, setLoadingCheckApprover, _);
    }, [shouldCheckApprover]);

    const scrollToValidation = useCallback(() => {
        if (!validationObj?.length)
            return;

        setTimeout(function () { //Start the timer
            const scrollObject = document.getElementById("validationAlert");
            if (!scrollObject) return;
            scrollObject.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }.bind(this), 500);
    }, [validationObj]);

    useEffect(() => {
        if (!showValidation)
            return;

        setShowValidation(false);
        scrollToValidation();
    }, [showValidation]);

    const handleSave = useCallback((submitApproval = false) => {
        const validationResult = validate(itemInEdit, false, false);

        if (submitApproval && !itemInEdit?.approverName && !itemInEdit?.autoApproved)
            validationResult.push({
                type: "error",
                message: "Approver has not been set. Please contact your administrator.",
            });

        setValidationObj([...validationResult]);

        if (validationResult?.length)
        {
            setShowValidation(true);

            if (submitApproval)
                setPopupMessageObj(null);

            return;
        }

        setLoading(true);
        const url = `${process.env.REACT_APP_WEB_API_BASE_URI}/${apiUrl}${itemInEdit.terminationId ? `/${itemInEdit.terminationId}` : ""}`;
        const method = itemInEdit.terminationId ? "PUT" : "POST";

        api({
            url,
            method,
            data: composeDataToSend(itemInEdit, submitApproval),
        })
            .then(() => {
                setLoading(false);

                if (!hasFullAccess)
                {
                    history.push("/");
                    return;
                }

                returnToList(history);
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [canSave, itemInEdit, history, hasFullAccess]);

    const footer = useMemo(() => {
        return (
            <Row>
                <Col xs={12} className="d-flex flex-wrap justify-content-end">
                    <Row className="w-100 m-0 p-0 mb-4">
                        <Col xs={3} className="d-flex flex-wrap justify-content-start align-items-center m-0 p-0">
                            <Button
                                color="info"
                                type="button"
                                onClick={() => {
                                    if (!hasFullAccess)
                                    {
                                        history.push("/");
                                        return;
                                    }
                    
                                    returnToList(history);
                                }}
                            >
                                Close
                            </Button>
                        </Col>
                        <Col xs={9} className="d-flex flex-wrap justify-content-end align-items-center m-0 p-0">
                            <Button
                                color="default"
                                type="button"
                                onClick={() => handleSave(false)}
                                disabled={itemInEdit?.status?.lookupValue !== "Initial"}
                            >
                                Save
                            </Button>
                            <Button
                                color="default"
                                type="button"
                                onClick={() => {
                                    setLoading(true);
                                    const validationResult = validate(itemInEdit, false, false);
                                    setValidationObj([...validationResult]);

                                    if (validationResult?.length)
                                    {
                                        setShowValidation(true);
                                        setLoading(false);
                                        return;
                                    }

                                    setLoading(false);
                                    setPopupMessageObj({
                                        type: "confirmation",
                                        message: "Are you sure you want to submit this request for approval?",
                                    });
                                }}
                                disabled={itemInEdit?.status?.lookupValue !== "Initial"}
                            >
                                Save and Submit Approval
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    }, [itemInEdit, history, canSave, hasFullAccess]);

    if (!hasAccess)
        return (
            <Redirect
                to={{
                    pathname: '/unauthorized',
                    state: {
                        from: "exitform",
                    }
                }}
            />
        );

    return (
        <section className="main">
            {(loading || formLoading || loadingCheckApprover) && <Loader />}
            <Container fluid>
                <Row className="m-0 p-0">
                    <Col xs={10}>
                        <TerminationForm
                            id={id}
                            isApproval={false}
                            itemInEdit={itemInEdit}
                            setItemInEdit={setItemInEdit}
                            setLoading={setFormLoading}
                            setCanSave={setCanSave}
                            setShouldCheckApprover={setShouldCheckApprover}
                            isFinal={false}
                            validationObj={validationObj}
                            footer={footer}
                        />
                    </Col>
                    <Col xs={2} className="bg-secondary lrfstatusbox" style={{minHeight: "30vh"}}>
                        <TerminationApprovalPanel
                            itemInEdit={itemInEdit}
                            loading={formLoading}
                            initialApprover={initialApprover}
                            isApproverExists={isApproverExists}
                        />
                    </Col>
                </Row>
            </Container>
        {
            popupMessageObj?.type === "confirmation" ?
            <ConfirmGeneralAlert
                onConfirm={() => handleSave(true)}
                onCancel={() => setPopupMessageObj(null)}
                textConfirm="Yes"
                title="Exit Form Submit for Approval Confirmation"
                textDesc={popupMessageObj.message}
            />
            :
            null
        }
        </section>
    );
};